import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api.js';

const MB_KEY = ['monthly-budgets'];

function invalidateAll(qc) {
  qc.invalidateQueries({ queryKey: MB_KEY });
  qc.invalidateQueries({ queryKey: ['forecasts'] });
  qc.invalidateQueries({ queryKey: ['cash-flow'] });
}

/**
 * Query — liste les budgets mensuels.
 * @param {Object} opts
 * @param {string} [opts.status='active'] — 'active' | 'archived'
 * @param {string} [opts.category_id]     — filtrer par catégorie
 */
export function useMonthlyBudgets({ status = 'active', category_id } = {}) {
  const params = new URLSearchParams({ status });
  if (category_id) params.set('category_id', category_id);
  return useQuery({
    queryKey: [...MB_KEY, { status, category_id }],
    queryFn: () => api.get(`/monthly-budgets?${params.toString()}`),
    staleTime: 60_000,
  });
}

/**
 * Mutation — POST /monthly-budgets
 * body : { name, category_id, budget_cents_monthly, start_date, end_date?, vat_rate?, notes? }
 */
export function useCreateMonthlyBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/monthly-budgets', body),
    onSuccess: () => invalidateAll(qc),
  });
}

/**
 * Mutation — PATCH /monthly-budgets/:id
 * body : patch partiel
 */
export function useUpdateMonthlyBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }) => api.patch(`/monthly-budgets/${id}`, patch),
    onSuccess: () => invalidateAll(qc),
  });
}

/**
 * Mutation — DELETE /monthly-budgets/:id (soft → archived)
 */
export function useArchiveMonthlyBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/monthly-budgets/${id}`),
    onSuccess: () => invalidateAll(qc),
  });
}
